import React, { useState, useRef, useEffect } from 'react';
import { Box, Paper, Grid, InputBase, Button, Typography, Popover, Checkbox, Avatar, MenuItem, Collapse, IconButton } from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import {
    SearchOutlinedIcon,
    KeyboardArrowDownOutlinedIcon,
    FilterAltOutlinedIcon,
    FilterListIcon,
    CloseIcon,
    SearchIcon
} from 'icons/materialIcons';
import TeamAddMembersModal from 'team/shared/features/addMember/TeamAddMembersModal';
import TeamMembersSortModal from './TeamMembersSortModal';
import { getTeamSubteams } from 'team/apis/teamSubteamApi';
import { getTemplates } from 'team/apis/teamTemplateApi';
import { useTeam } from 'team/contexts/teamContext';
import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';

const TeamMembersNavbar = ({
    onSearch,
    membersCount = 200,
    onAddMemberSelect,
    currentSort,
    onSortChange,
    viewMode = 'grid',
    onViewModeChange,
    filters = { subteams: [], templates: [], adminStatus: [], assignments: [], accessoryStatus: [] },
    filterSearch = '',
    onFilterChange,
    onFilterSearchChange,
    onClearFilters
}) => {
    const { currentTeamId } = useTeam();
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const [sortButtonAnchorEl, setSortButtonAnchorEl] = useState(null);
    const [filterButtonAnchorEl, setFilterButtonAnchorEl] = useState(null);
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [subteamsList, setSubteamsList] = useState([]);
    const [templatesList, setTemplatesList] = useState([]);
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [localFilterSearch, setLocalFilterSearch] = useState(filterSearch || '');

    const addButtonRef = useRef(null);
    const filterButtonRef = useRef(null);

    const handleAddMemberModalOpen = (event) => {
        addButtonRef.current = event.currentTarget;
        setIsAddMemberModalOpen(true);
    };

    const handleAddMemberModalClose = () => {
        setIsAddMemberModalOpen(false);
    };

    const handleAddMemberSelect = (option) => {
        setIsAddMemberModalOpen(false);
        if (onAddMemberSelect) {
            onAddMemberSelect(option);
        }
    };

    const handleSearchChange = (event) => {
        onSearch(event.target.value);
    };

    const handleSortClick = (event) => {
        setSortButtonAnchorEl(event.currentTarget);
        setIsSortModalOpen(true);
    };

    const handleSortModalClose = () => {
        setIsSortModalOpen(false);
        setSortButtonAnchorEl(null);
    };

    const handleApplySort = (sortValue) => {
        if (onSortChange) {
            onSortChange(sortValue);
        }
    };

    const handleFilterClick = (event) => {
        setFilterButtonAnchorEl(event.currentTarget);
        setFilterPopoverOpen(true);
    };

    const handleFilterClose = () => {
        setFilterPopoverOpen(false);
        setFilterButtonAnchorEl(null);
        setOpenDropdown(null);
    };

    const handleLocalFilterSearchChange = (event) => {
        const value = event.target.value;
        setLocalFilterSearch(value);
        if (onFilterSearchChange) {
            onFilterSearchChange(value);
        }
    };

    const handleFilterOptionClick = (option, event) => {
        event.stopPropagation();
        setOpenDropdown(openDropdown === option ? null : option);
    };

    const handleItemToggle = (option, itemId) => {
        const currentSelection = filters[option] || [];
        const isSelected = currentSelection.includes(itemId);
        const newSelection = isSelected
            ? currentSelection.filter(id => id !== itemId)
            : [...currentSelection, itemId];

        if (onFilterChange) {
            onFilterChange(option, newSelection);
        }
    };

    const handleClearFilters = () => {
        setLocalFilterSearch('');
        setOpenDropdown(null);
        if (onClearFilters) {
            onClearFilters();
        }
    };

    // Fetch subteams
    useEffect(() => {
        const fetchSubteams = async () => {
            if (!filterPopoverOpen || !currentTeamId) return;
            setLoadingSubteams(true);
            try {
                const response = await getTeamSubteams(currentTeamId);
                if (response && response.success && response.subteams) {
                    setSubteamsList(response.subteams);
                } else if (Array.isArray(response)) {
                    setSubteamsList(response);
                }
            } catch (error) {
                console.error('Error fetching subteams:', error);
            } finally {
                setLoadingSubteams(false);
            }
        };
        fetchSubteams();
    }, [filterPopoverOpen, currentTeamId]);

    // Fetch templates
    useEffect(() => {
        const fetchTemplates = async () => {
            if (!filterPopoverOpen || !currentTeamId) return;
            setLoadingTemplates(true);
            try {
                const response = await getTemplates(currentTeamId);
                if (response && response.success && response.templates) {
                    setTemplatesList(response.templates);
                }
            } catch (error) {
                console.error('Error fetching templates:', error);
            } finally {
                setLoadingTemplates(false);
            }
        };
        fetchTemplates();
    }, [filterPopoverOpen, currentTeamId]);

    return (
        <Box sx={{ padding: '1rem' }}>
            {/* First Row: Title, Filter/Sort Buttons, and Add Members Button */}
            <Grid container spacing={2} alignItems="center" sx={{ marginBottom: '1rem' }}>
                {/* Left Side - Title */}
                <Grid item xs={12} sm="auto">
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Members{' '}
                        <Typography component="span" variant="body2" sx={{ fontWeight: 400, color: '#757575' }}>
                            ({membersCount})
                        </Typography>
                    </Typography>
                </Grid>

                {/* Right Side - Filter, Sort, and Add Members Buttons */}
                <Grid item xs={12} sm container justifyContent="flex-end">
                    <Grid container spacing={1} justifyContent="flex-end">
                        {/* Grid View Button */}
                        <Grid item>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '1.25rem',
                                    border: '0.0625rem solid #dddddd',
                                    backgroundColor: 'transparent',
                                    padding: 0,
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: '100%',
                                        width: '50%',
                                        border: '0.0625rem solid #dddddd',
                                        borderRadius: '1.25rem',
                                        backgroundColor: '#fff',
                                        transform: viewMode === 'list' ? 'translateX(0)' : 'translateX(100%)',
                                        transition: 'transform 300ms ease',
                                        pointerEvents: 'none'
                                    }}
                                />
                                <Button
                                    onClick={() => onViewModeChange && onViewModeChange('list')}
                                    disableRipple
                                    startIcon={<ViewStreamOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: '#4f4f4f',
                                        borderRadius: '1.25rem',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        padding: '0.5rem 0.9rem',
                                        minWidth: '5.5rem',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                        },
                                        '& .MuiButton-startIcon': {
                                            mr: 1,
                                        }
                                    }}
                                >
                                    List
                                </Button>
                                <Button
                                    onClick={() => onViewModeChange && onViewModeChange('grid')}
                                    disableRipple
                                    startIcon={<GridViewOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: '#4f4f4f',
                                        borderRadius: '1.25rem',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        padding: '0.5rem 0.9rem',
                                        minWidth: '5.5rem',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                        },
                                        '& .MuiButton-startIcon': {
                                            mr: 1,
                                        }
                                    }}
                                >
                                    Grid
                                </Button>
                            </Box>
                        </Grid>

                        {/* Filter Button */}
                        <Grid item>
                            <Button
                                variant="contained"
                                ref={filterButtonRef}
                                startIcon={<FilterAltOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                onClick={handleFilterClick}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: '0.0625rem solid #e3e3e3',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '6rem',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Grid>

                        {/* Sort Button */}
                        <Grid item>
                            <Button
                                variant="contained"
                                startIcon={<FilterListIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                onClick={handleSortClick}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: '0.0625rem solid #e3e3e3',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '6rem',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Sort
                            </Button>
                        </Grid>

                        {/* Add Members Button */}
                        <Grid item>
                            <Button
                                ref={addButtonRef}
                                variant="contained"
                                onClick={handleAddMemberModalOpen}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    fontWeight: '500',
                                    borderRadius: '0.5rem',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                endIcon={<KeyboardArrowDownOutlinedIcon />}
                            >
                                Add Members
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Second Row: Search Bar */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#ffff',
                            border: '1px solid #dfdfdf',
                            borderRadius: '1.5rem',
                            padding: '0.25rem 1rem',
                            width: '100%'
                        }}
                    >
                        <SearchOutlinedIcon sx={{ color: '#828282', marginRight: 1 }} />
                        <InputBase
                            placeholder="Search by name, job title, or email"
                            sx={{ width: '100%' }}
                            onChange={handleSearchChange}
                        />
                    </Box>
                </Grid>
            </Grid>

            <TeamAddMembersModal
                isOpen={isAddMemberModalOpen}
                onClose={handleAddMemberModalClose}
                onSelect={handleAddMemberSelect}
                anchorEl={addButtonRef.current}
            />

            <TeamMembersSortModal
                open={isSortModalOpen}
                onClose={handleSortModalClose}
                currentSort={currentSort}
                onApplySort={handleApplySort}
                anchorEl={sortButtonAnchorEl}
            />

            {/* Filter Popover */}
            <Popover
                open={filterPopoverOpen}
                onClose={handleFilterClose}
                anchorEl={filterButtonAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: '12px',
                        minWidth: 300,
                        maxWidth: 400,
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Search Field */}
                    <Box sx={{
                        mb: 2,
                        '& .MuiInputBase-root': {
                            backgroundColor: '#f7f7f7',
                            borderRadius: '20px',
                            padding: '0.5rem 1rem',
                            width: '100%',
                            fontSize: '0.7rem',
                            color: '#4f4f4f',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#A0A0A0',
                                fontWeight: '400',
                                opacity: 1
                            }
                        }
                    }}>
                        <InputBase
                            placeholder="Search team members..."
                            value={localFilterSearch}
                            onChange={handleLocalFilterSearchChange}
                        />
                    </Box>

                    {/* Filter Options */}
                    <Box sx={{ mb: 2 }}>
                        {[
                            { key: 'subteams', label: 'Subteams' },
                            { key: 'templates', label: 'Templates' },
                            { key: 'adminStatus', label: 'Admin Status' },
                            { key: 'assignments', label: 'Assignments' },
                            { key: 'accessoryStatus', label: 'Accessory Activation' }
                        ].map((option) => {
                            const searchTerm = localFilterSearch.toLowerCase();

                            // Detect matches for auto-expand
                            const matchingItems = (() => {
                                switch (option.key) {
                                    case 'subteams':
                                        return subteamsList.filter(s => s.name.toLowerCase().includes(searchTerm));
                                    case 'templates':
                                        return templatesList.filter(t => t.templateName.toLowerCase().includes(searchTerm));
                                    case 'adminStatus':
                                        return [
                                            { value: 'fullTeamAdmin', label: 'Full Team Admin' },
                                            { value: 'subteamAdmin', label: 'Subteam Admin' },
                                            { value: 'nonAdmin', label: 'Non Admin' }
                                        ].filter(i => i.label.toLowerCase().includes(searchTerm));
                                    case 'assignments':
                                        return [
                                            { value: 'assignedToSubteam', label: 'Assigned to a Subteam' },
                                            { value: 'notAssignedToSubteam', label: 'Not Assigned to a Subteam' },
                                            { value: 'assignedToTemplate', label: 'Assigned to a Template' }
                                        ].filter(i => i.label.toLowerCase().includes(searchTerm));
                                    case 'accessoryStatus':
                                        return [
                                            { value: 'accessoryActivated', label: 'Accessory activated' },
                                            { value: 'noAccessoryActivated', label: 'No accessory activated' }
                                        ].filter(i => i.label.toLowerCase().includes(searchTerm));
                                    default:
                                        return [];
                                }
                            })();

                            const hasSearchMatches = searchTerm !== '' && matchingItems.length > 0;
                            const isDropdownOpen = openDropdown === option.key || hasSearchMatches;
                            const selectedCount = filters[option.key]?.length || 0;

                            return (
                                <Box key={option.key} sx={{ marginBottom: '0.75rem' }}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            borderRadius: '0.25rem',
                                            padding: '0.75rem 1rem',
                                            minHeight: '3rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0'
                                            }
                                        }}
                                        onClick={(e) => handleFilterOptionClick(option.key, e)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                color: '#666666',
                                                lineHeight: 1.5
                                            }}>
                                                {option.label}
                                            </Typography>
                                            {selectedCount > 0 && (
                                                <Typography sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    color: '#1976d2',
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px'
                                                }}>
                                                    {selectedCount}
                                                </Typography>
                                            )}
                                        </Box>
                                        <KeyboardArrowDownOutlinedIcon
                                            sx={{
                                                fontSize: '1.25rem',
                                                color: '#666',
                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease-in-out'
                                            }}
                                        />
                                    </Box>

                                    <Collapse in={isDropdownOpen} timeout="auto" unmountOnExit>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                mt: 0.5,
                                                maxHeight: 300,
                                                width: '100%',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                                                border: '1px solid #e0e0e0',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {option.key === 'subteams' && (
                                                loadingSubteams ? (
                                                    <MenuItem disabled sx={{ fontSize: '0.8rem' }}>Loading...</MenuItem>
                                                ) : subteamsList.length === 0 ? (
                                                    <MenuItem disabled sx={{ fontSize: '0.8rem' }}>No subteams found</MenuItem>
                                                ) : subteamsList.map((subteam) => (
                                                    <MenuItem
                                                        key={subteam._id}
                                                        onClick={() => handleItemToggle('subteams', subteam._id)}
                                                        sx={{ padding: '0.75rem 1rem' }}
                                                    >
                                                        <Checkbox
                                                            checked={filters.subteams.includes(subteam._id)}
                                                            sx={{
                                                                color: '#e0e0e0',
                                                                padding: '0',
                                                                marginRight: '12px',
                                                                '&.Mui-checked': { color: '#000' }
                                                            }}
                                                        />
                                                        <Avatar sx={{
                                                            width: 32,
                                                            height: 32,
                                                            marginRight: '12px',
                                                            bgcolor: getSubteamColor(subteam.name),
                                                            fontSize: '0.875rem'
                                                        }}>
                                                            {getSubteamInitials(subteam.name)}
                                                        </Avatar>
                                                        <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>{subteam.name}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}

                                            {option.key === 'templates' && (
                                                loadingTemplates ? (
                                                    <MenuItem disabled sx={{ fontSize: '0.8rem' }}>Loading...</MenuItem>
                                                ) : templatesList.length === 0 ? (
                                                    <MenuItem disabled sx={{ fontSize: '0.8rem' }}>No templates found</MenuItem>
                                                ) : templatesList.map((template) => (
                                                    <MenuItem
                                                        key={template._id}
                                                        onClick={() => handleItemToggle('templates', template._id)}
                                                        sx={{ padding: '0.75rem 1rem' }}
                                                    >
                                                        <Checkbox
                                                            checked={filters.templates.includes(template._id)}
                                                            sx={{
                                                                color: '#e0e0e0',
                                                                padding: '0',
                                                                marginRight: '12px',
                                                                '&.Mui-checked': { color: '#000' }
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>{template.templateName}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}

                                            {option.key === 'adminStatus' && (
                                                [
                                                    { value: 'fullTeamAdmin', label: 'Full Team Admin' },
                                                    { value: 'subteamAdmin', label: 'Subteam Admin' },
                                                    { value: 'nonAdmin', label: 'Non Admin' }
                                                ].map((item) => (
                                                    <MenuItem
                                                        key={item.value}
                                                        onClick={() => handleItemToggle('adminStatus', item.value)}
                                                        sx={{ padding: '0.75rem 1rem' }}
                                                    >
                                                        <Checkbox
                                                            checked={filters.adminStatus.includes(item.value)}
                                                            sx={{
                                                                color: '#e0e0e0',
                                                                padding: '0',
                                                                marginRight: '12px',
                                                                '&.Mui-checked': { color: '#000' }
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>{item.label}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}

                                            {option.key === 'assignments' && (
                                                [
                                                    { value: 'assignedToSubteam', label: 'Assigned to a Subteam' },
                                                    { value: 'notAssignedToSubteam', label: 'Not Assigned to a Subteam' },
                                                    { value: 'assignedToTemplate', label: 'Assigned to a Template' }
                                                ].map((item) => (
                                                    <MenuItem
                                                        key={item.value}
                                                        onClick={() => handleItemToggle('assignments', item.value)}
                                                        sx={{ padding: '0.75rem 1rem' }}
                                                    >
                                                        <Checkbox
                                                            checked={filters.assignments.includes(item.value)}
                                                            sx={{
                                                                color: '#e0e0e0',
                                                                padding: '0',
                                                                marginRight: '12px',
                                                                '&.Mui-checked': { color: '#000' }
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>{item.label}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}

                                            {option.key === 'accessoryStatus' && (
                                                [
                                                    { value: 'accessoryActivated', label: 'Accessory activated' },
                                                    { value: 'noAccessoryActivated', label: 'No accessory activated' }
                                                ].map((item) => (
                                                    <MenuItem
                                                        key={item.value}
                                                        onClick={() => handleItemToggle('accessoryStatus', item.value)}
                                                        sx={{ padding: '0.75rem 1rem' }}
                                                    >
                                                        <Checkbox
                                                            checked={filters.accessoryStatus.includes(item.value)}
                                                            sx={{
                                                                color: '#e0e0e0',
                                                                padding: '0',
                                                                marginRight: '12px',
                                                                '&.Mui-checked': { color: '#000' }
                                                            }}
                                                        />
                                                        <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>{item.label}</Typography>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Paper>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            sx={{
                                backgroundColor: 'white',
                                color: '#828282',
                                borderRadius: '20px',
                                border: 'none',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: 'white',
                                    boxShadow: 2,
                                }
                            }}
                        >
                            Clear filter
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleFilterClose}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                }
                            }}
                        >
                            Apply filter
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default TeamMembersNavbar;
